import { useState, useCallback, useEffect, useRef } from 'react';
import styles from './SelectionBox.module.css';
import { useDesktopFocus } from '../Window/WindowManager';

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function toRect(
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
): Rect {
  return {
    x: Math.min(startX, currentX),
    y: Math.min(startY, currentY),
    width: Math.abs(currentX - startX),
    height: Math.abs(currentY - startY),
  };
}

export function SelectionBox() {
  const focusDesktop = useDesktopFocus();
  const overlayRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const startRef = useRef({ x: 0, y: 0 });

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only primary button (left click / primary touch)
      if (e.button !== 0) return;

      e.preventDefault();
      focusDesktop();

      const overlay = overlayRef.current;
      if (!overlay) return;

      const bounds = overlay.getBoundingClientRect();
      const x = e.clientX - bounds.left;
      const y = e.clientY - bounds.top;

      startRef.current = { x, y };
      setRect({ x, y, width: 0, height: 0 });
      setDragging(true);
    },
    [focusDesktop],
  );

  useEffect(() => {
    if (!dragging) return;

    const overlay = overlayRef.current;
    if (!overlay) return;

    const bounds = overlay.getBoundingClientRect();

    const handlePointerMove = (e: PointerEvent) => {
      const currentX = Math.max(0, Math.min(e.clientX - bounds.left, bounds.width));
      const currentY = Math.max(0, Math.min(e.clientY - bounds.top, bounds.height));
      setRect(toRect(startRef.current.x, startRef.current.y, currentX, currentY));
    };

    const handlePointerUp = () => {
      setDragging(false);
      setRect(null);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [dragging]);

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      onPointerDown={handlePointerDown}
    >
      {rect && rect.width + rect.height > 2 && (
        <div
          className={styles.selection}
          style={{
            left: rect.x,
            top: rect.y,
            width: rect.width,
            height: rect.height,
          }}
        />
      )}
    </div>
  );
}
